/**
 * El gestor de plantillas.
 *
 * POR QUÉ HACÍA FALTA UNA PANTALLA Y NO BASTABA EL DESPLEGABLE
 * ───────────────────────────────────────────────────────────
 * Con solo un desplegable en el alta, una plantilla se puede usar pero no se
 * puede **mirar**: no hay forma de saber qué trae dentro antes de aplicarla, ni
 * de borrar la que salió mal, ni de darse cuenta de que hay tres casi iguales.
 * Y una plantilla que nadie revisa se convierte en el sitio donde un error se
 * multiplica por todos los negocios que salgan de ella.
 *
 * LO QUE ESTA PANTALLA ENSEÑA Y NO SE VE EN OTRO SITIO
 * ───────────────────────────────────────────────────
 * Que una plantilla **no lleva identidad**. Se dice a las claras, porque es la
 * pregunta que se hace todo el mundo la primera vez: «¿esto se va a llevar la
 * cuenta de Chatwoot del negocio del que la saqué?». No.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { useSesion } from '../../auth'
import { Button } from '../../components/ui/button'
import { api } from '../../lib/api'

interface PlantillaFila {
    id: number
    nombre: string
    sector: string | null
    ficha: Record<string, unknown>
    creadaEn: string
}

/** Un resumen de lo que trae, sin abrir nada. */
function resumen(f: Record<string, unknown>): string[] {
    const partes: string[] = []
    const voz = f['voz'] as { tratamiento?: string; emojis?: string } | undefined
    const voc = f['vocabulario'] as { cliente?: string } | undefined
    const cat = f['catalogo'] as { servicios?: unknown[]; profesionales?: unknown[] } | undefined
    const con = f['conocimiento'] as { faq?: unknown[] } | undefined

    if (voc?.cliente) partes.push(`llama «${voc.cliente}» a sus clientes`)
    if (voz?.tratamiento) partes.push(voz.tratamiento === 'usted' ? 'trata de usted' : 'tutea')
    if (cat?.servicios?.length) partes.push(`${cat.servicios.length} servicios`)
    if (cat?.profesionales?.length) partes.push(`${cat.profesionales.length} profesionales`)
    if (con?.faq?.length) partes.push(`${con.faq.length} preguntas frecuentes`)
    return partes
}

export function Plantillas() {
    const { data: sesion } = useSesion()
    const [filas, setFilas] = useState<PlantillaFila[]>([])
    const [cargando, setCargando] = useState(true)
    const [abierta, setAbierta] = useState<number | null>(null)

    async function recargar() {
        try {
            const r = await api.get<{ plantillas: PlantillaFila[] }>('/altas/plantillas')
            setFilas(r.plantillas)
        } finally {
            setCargando(false)
        }
    }

    useEffect(() => {
        void recargar()
    }, [])

    if (sesion && sesion.role !== 'admin') {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }

    async function borrar(id: number, nombre: string) {
        if (!confirm(`¿Borrar la plantilla «${nombre}»? Los negocios ya creados con ella no se tocan.`)) return
        await api.delete(`/altas/plantillas/${id}`)
        void recargar()
    }

    return (
        <div className="mx-auto w-full max-w-[1700px] space-y-6">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <Link to="/altas" className="text-xs text-neutral-500 hover:text-neutral-300">
                        ← Altas
                    </Link>
                    <h1 className="text-lg font-semibold text-neutral-100">Plantillas de alta</h1>
                    <p className="mt-1 text-sm text-neutral-400">
                        Un negocio ya montado, guardado para repetirlo. De aquí salen las demos por
                        sector.
                    </p>
                </div>
                <Link
                    to="/altas/nueva"
                    className="rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-white"
                >
                    Nueva alta
                </Link>
            </header>

            <p className="rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-xs text-neutral-400">
                Una plantilla <strong>no lleva identidad</strong>: ni la cuenta de canal, ni las
                credenciales, ni el calendario del negocio del que salió. Si los llevara, el segundo
                negocio creado con ella apuntaría al buzón del primero — y el bot del primero dejaría
                de atender.
            </p>

            {cargando ? (
                <p className="text-sm text-neutral-500">Cargando…</p>
            ) : filas.length === 0 ? (
                <div className="rounded-lg border border-dashed border-neutral-800 p-8 text-center">
                    <p className="text-sm text-neutral-400">Todavía no hay ninguna.</p>
                    <p className="mt-1 text-xs text-neutral-500">
                        Se guardan desde el alta: rellena un negocio, revísalo y dale a «Guardar
                        plantilla».
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filas.map((p) => (
                        <section
                            key={p.id}
                            className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/40"
                        >
                            <div className="flex items-start gap-3 px-4 py-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-700 bg-neutral-950/60">
                                    📋
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-neutral-100">{p.nombre}</span>
                                        {p.sector && (
                                            <span className="rounded bg-neutral-800 px-1.5 py-0.5 text-[10px] text-neutral-400">
                                                {p.sector}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-0.5 text-xs text-neutral-500">
                                        {resumen(p.ficha).join(' · ') || 'sin contenido'}
                                    </p>
                                </div>
                                <div className="flex shrink-0 gap-2">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setAbierta(abierta === p.id ? null : p.id)}
                                    >
                                        {abierta === p.id ? 'Ocultar' : 'Ver qué trae'}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => void borrar(p.id, p.nombre)}
                                    >
                                        Borrar
                                    </Button>
                                </div>
                            </div>
                            {abierta === p.id && (
                                <pre className="max-h-96 overflow-auto border-t border-neutral-800 bg-neutral-950/60 px-4 py-3 text-xs text-neutral-300">
                                    {JSON.stringify(p.ficha, null, 2)}
                                </pre>
                            )}
                        </section>
                    ))}
                </div>
            )}
        </div>
    )
}
